import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactToPrint from 'react-to-print';

import { Box, Button, Grid, Typography } from '@mui/material';

import { GenericModalFooter } from '@/components/modal/GenericModalFooter';
import type { TStepComponentProps } from '@/components/stepper/types';

import type { TCreateStationForm, TCreateStationResult } from '@/types/station';

import { prepareStationDetails, TPreparedDetailsItem } from '../../helpers';
import { styles as formStyles } from '../Form.styles';

export const Completion = ({ data, onSubmit }: TStepComponentProps<TCreateStationForm>) => {
  const { t } = useTranslation();
  const stationDetailsRef = useRef(null);

  const preparedDetails = prepareStationDetails(data.result as TCreateStationResult);

  // Guards a double click on "Complete": finishing opens the new site and the success dialog, which
  // must happen once. Stays true on success (the wizard closes); reset only if finishing fails.
  const [finishing, setFinishing] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (finishing) {
      return;
    }

    setFinishing(true);

    try {
      await onSubmit();
    } catch (error) {
      setFinishing(false);
      throw error;
    }
  };

  const renderGridItem = ({ label, value, gridProps }: TPreparedDetailsItem) => (
    <Grid item flexDirection="column" {...gridProps} key={label}>
      <Typography variant="caption" color="text.secondary">
        {t(label)}
      </Typography>
      <Typography fontWeight={500}>{value}</Typography>
    </Grid>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={formStyles.form}>
      <Box sx={formStyles.content}>
        <Typography variant="h6" sx={formStyles.title}>
          {t('stationSetup.complete.title')}
        </Typography>

        <Typography color="text.secondary" marginBottom={6}>
          {t('stationSetup.complete.details')}
        </Typography>
        <Grid container spacing={4} ref={stationDetailsRef}>
          {preparedDetails.map(renderGridItem)}
        </Grid>
        <ReactToPrint
          trigger={() => (
            <Button variant="text" sx={{ marginTop: 7, marginBottom: 8 }}>
              {t('common.print')}
            </Button>
          )}
          pageStyle="margin:24px"
          content={() => stationDetailsRef.current}
        />
        <Typography color="text.secondary">
          {t('stationSetup.complete.rebootSystemInfo')}
        </Typography>
      </Box>

      <GenericModalFooter sx={formStyles.footer} squareButtons>
        <Box sx={formStyles.controls}>
          <Button type="submit" disabled={finishing}>
            {t('common.complete')}
          </Button>
        </Box>
      </GenericModalFooter>
    </Box>
  );
};
